import { FiGlobe } from "@react-icons/all-files/fi/FiGlobe";
import { HiCheck } from "@react-icons/all-files/hi/HiCheck";
import { HiOutlineClipboardCopy } from "@react-icons/all-files/hi/HiOutlineClipboardCopy";
import { RiInstagramFill } from "@react-icons/all-files/ri/RiInstagramFill";
import { RiMediumFill } from "@react-icons/all-files/ri/RiMediumFill";
import { RiTwitterFill } from "@react-icons/all-files/ri/RiTwitterFill";
import { bulkFilter } from "bc-graphql-filter";
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from "next";
import { useAuthUser, withAuthUser } from "next-firebase-auth";
import { useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import Head from "next/head";
import Link from "next/link";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.min.css";
import useCopyClipboard from "react-use-clipboard";
import Avatar from "../../common/components/Avatar";
import Button from "../../common/components/Button";
import Header from "../../common/components/Header";
import SmartImage from "../../common/components/SmartImage";
import { Art, Collection, LOCALE_OPTIONS } from "../../common/lib/api/base";
import { useBreakpoint } from "../../common/lib/hooks/useWindow";
import { makeAdmin } from "../../common/lib/makeAdmin";
import {
  getArtLowQualUrl,
  getArtUrl,
  isAdmin,
  makeStaticPathsLocale,
  prettifyWalletHash,
  toDocs,
} from "../../common/lib/utils";
import {
  ARTIST_FORM_ENABLER_PROPS,
  ARTIST_FORM_RESULT,
} from "../../forms/artistForm";
import styles from "../../styles/Artist.module.scss";
//@ts-ignore

const ArtistFormComponent = dynamic<ARTIST_FORM_ENABLER_PROPS>(
  () =>
    import("../../forms/artistForm").then(
      ({ ArtistFormEnabler }) => ArtistFormEnabler
    ),
  { ssr: false }
);

const BULK_FILTER = {
  artist: `
      {
        name
        id
        slug
        description
        image_url
        wallet_address
        instagram_username
        twitter_username
        medium_username
        external_url
        banner_image_url
        featured
      }
  `,
  arts: `
      {
          id
          name
          image_url
          image_preview_url
          image_thumbnail_url
          animation_url
          animation_original_url
          slug
      }
    `,
};

export const getStaticProps: GetStaticProps<{
  artist: Collection;
  arts: Art[];
}> = async (ctx) => {
  try {
    const locale = ctx.locale as LOCALE_OPTIONS;
    const { makeAdmin } = await import("../../common/lib/makeAdmin");
    const admin = await makeAdmin();
    const store = admin.firestore();
    const col = store.collection("collections");
    const artApi = store.collection("arts");
    const slug = (ctx.params?.slug as string) || "";
    const [artist, arts, collabs] = await Promise.all([
      col
        .doc(slug)
        .get()
        .then((snapshot) => {
          if (!snapshot.exists)
            throw new Error("Trying to load artist that doesnt exist");
          return snapshot.data() as Collection;
        }),
      toDocs<Art>(artApi.where("collection.slug", "==", slug).get()),
      toDocs<{ id: string }>(
        col.doc(slug).collection("collabs").select().get()
      ).then((ids) => {
        const idArray = ids.map(({ id }) => id);
        if (idArray.length > 0)
          return toDocs<Art>(artApi.where("__name__", "in", idArray).get());
        return [];
      }),
    ]);
    const data = {
      artist: {
        ...artist,
        ...artist?.overrides?.[locale],
      },
      arts: arts
        .map((art) => ({ ...art, ...art.overrides?.[locale] }))
        .concat(collabs.map((art) => ({ ...art, ...art.overrides?.[locale] }))),
    };
    type DATA = typeof data;
    return {
      props: await bulkFilter<DATA, DATA>(data, BULK_FILTER),
      revalidate: 60,
    };
  } catch (e) {
    console.log(e);
    return {
      notFound: true,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const admin = await makeAdmin();
    const store = admin.firestore();
    const ids = await toDocs<{ id: string }>(
      // @ts-ignore
      store.collection("collections").select().get()
    );
    return {
      paths: makeStaticPathsLocale(
        ids.map(({ id }) => ({
          params: {
            slug: id,
          },
        }))
      ),
      fallback: "blocking",
    };
  } catch (e) {
    return {
      paths: [],
      fallback: "blocking",
    };
  }
};

type ARTIST_PAGE_PROPS = InferGetStaticPropsType<typeof getStaticProps>;
type SOCIALS_PROPS = Required<Pick<ARTIST_PAGE_PROPS, "artist">>;

const Socials: React.FC<SOCIALS_PROPS> = ({ artist }) => {
  const t = useTranslations("Artist");
  if (
    !artist.instagram_username &&
    !artist.medium_username &&
    !artist.twitter_username
  )
    return null;
  return (
    <div className={styles.socials}>
      <h3>{t("socials")}</h3>
      {artist.instagram_username && (
        <a
          href={`https://www.instagram.com/${artist.instagram_username}`}
          target="_blank"
          referrerPolicy="no-referrer"
        >
          <Button
            title={"Instagram"}
            variant={"white-filled"}
            icon={RiInstagramFill}
            className={styles.socials_button}
          />
        </a>
      )}
      {artist.twitter_username && (
        <a
          href={`https://twitter.com/${artist.twitter_username}`}
          target="_blank"
          referrerPolicy="no-referrer"
        >
          <Button
            title={"Twitter"}
            variant={"white-filled"}
            icon={RiTwitterFill}
            className={styles.socials_button}
          />
        </a>
      )}
      {artist.medium_username && (
        <a
          href={`https://${artist.medium_username}.medium.com`}
          target="_blank"
          referrerPolicy="no-referrer"
        >
          <Button
            title={"Medium"}
            variant={"white-filled"}
            icon={RiMediumFill}
            className={styles.socials_button}
          />
        </a>
      )}
      {artist.external_url && (
        <a
          href={artist.external_url.replace(/^https?/, "https")}
          target="_blank"
          referrerPolicy="no-referrer"
        >
          <Button
            title={"Website"}
            variant={"white-filled"}
            icon={FiGlobe}
            className={styles.socials_button}
          />
        </a>
      )}
    </div>
  );
};

const Artist: React.FC<ARTIST_PAGE_PROPS> = (props) => {
  const t = useTranslations("Artist");
  const isMedium = useBreakpoint("MEDIUM");
  const [data, setData] = useState<ARTIST_FORM_RESULT | null>(null);
  const { artist: propsArtist, arts: propsArts } = props;
  const user = useAuthUser();
  const { arts: dataArts = [], ...rest } = data || {};
  const artist = { ...propsArtist, ...rest } as Collection;
  const [isCopied, setCopied] = useCopyClipboard(
    artist.wallet_address || "0x0000000000",
    {
      successDuration: 2000,
    }
  );
  return (
    <>
      <Head>
        <title>{artist.name} - Spaceship Art Gallery </title>
      </Head>
      <Header className={styles.dark_shadow} />
      {isAdmin(user) && (
        <ArtistFormComponent
          artist={propsArtist}
          arts={propsArts}
          onDataChange={setData}
        />
      )}
      <div className={styles.art_wrapper}>
        <div>
          <SmartImage
            aspectRatio={1400 / 220}
            placeholder={`data:image/png;base64, iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mOMrwcAAUMA4McNmjsAAAAASUVORK5CYII=`}
            src={
              artist.banner_image_url ||
              "https://via.placeholder.com/1400x220.png?text=Lembrar+o+artista+de+colocar+capa"
            }
          />
        </div>
      </div>
      <div className={styles.container}>
        <main>
          <div className={styles.artist_name}>
            <div className={styles.avatar}>
              <Avatar src={artist.image_url} size={isMedium ? 120 : 75} />
            </div>
            <div>
              <h2>{artist.name}</h2>
              <Button
                title={prettifyWalletHash(
                  artist.wallet_address || "0x000000000000"
                )}
                variant="white"
                icon={isCopied ? HiCheck : HiOutlineClipboardCopy}
                onClick={() => {
                  setCopied();
                  toast("Wallet address copied to clipboard", {
                    position: "bottom-center",
                    hideProgressBar: true,
                  });
                }}
                style={{ fontFamily: "monospace", fontSize: "0.85em" }}
              />

              {/* <p>
                  0x0000...00
                  {artist.payout_address.slice(0, 6) +
                    "..." +
                    artist.payout_address.slice(-2)}{" "}
                  <HiOutlineClipboardCopy />
                </p> */}
            </div>
          </div>
          <div className={styles.content}>
            <div>
              <div className={styles.artist_info}>
                <div className={styles.biography}>
                  <h3>{t("bio")}</h3>
                  <ReactMarkdown>{artist.description}</ReactMarkdown>
                </div>
                {isMedium && <Socials artist={artist} />}
              </div>
              {/* <div className={style.follows}>
              <div>
                <h2>334</h2>
                <p>Followers</p>
              </div>
              <div>
                <h2>123</h2>
                <p>Following</p>
              </div>
              <Button title="Follow" variant="white-filled" />
            </div> */}
            </div>
            <div>
              <div>
                <h3 style={{ marginBottom: 12 }}>{t("artworks")}</h3>
              </div>

              <div className={styles.artist_images}>
                {propsArts.map((art) => {
                  const { slug, name } = art;
                  return (
                    <div key={slug}>
                      <Link href={`/arts/${slug}`}>
                        <a>
                          <div>
                            <SmartImage
                              src={getArtUrl(art)}
                              placeholder={getArtLowQualUrl(art)}
                              width={"100%"}
                              className={styles.glow}
                            />
                            <h2 className={styles.art_title}>{name}</h2>
                          </div>
                        </a>
                      </Link>
                    </div>
                  );
                })}
              </div>
              {!isMedium && <Socials artist={artist} />}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default withAuthUser<ARTIST_PAGE_PROPS>()(Artist);
